import React, { useContext, useEffect, useRef, useState } from "react";
import { keyframes } from "@emotion/react";
import { styled } from "@mui/material/styles";
import PropTypes from "prop-types";
import { MapContext } from "../../contexts";
import { useGoogleMaps } from "../../hooks/useGoogleMaps";
import {
  Box,
  useMediaQuery,
  useTheme,
  Typography,
  Button,
} from "@mui/material/";

import { COUNTRY_MAP_STYLES, MAPCOLORS } from "./mapstyles";
import countryDrawer from "../../util/lang/countryDrawer";
import acceptanceLevels from "../../util/lang/acceptanceLevels";
import MapSearchHeader, {
  COUNTRY_HEADLINE_FOOTNOTE_ADDITIONAL_ID,
  COUNTRY_HEADLINE_FOOTNOTE_ID,
} from "../MapSearchHeader";
import { FOOTNOTE_SCROLL_OFFSET_PX } from "../Faqs/faqFootnotes";
import AcceptanceMarks from "../AcceptanceMarks";
import RegionsSquares from "./RegionsSquares";
import {
  makeCountryMap,
  assignAcceptanceMapData,
  countryView_addListeners,
  getInitialZoom,
  createCustomZoomControl,
  syncMapSelectionToCountry,
  handleCountryDetailsModalClose,
  countryClicked,
} from "./mapmethods";
import { Helmet } from "react-helmet";

import Header from "../Header";
import Footer from "../Footer";
import MapLegend from "../MapLegend";
import CountryDetailsModal from "../CountryDetailsModal";
// https://github.com/nvkelso/natural-earth-vector/tree/master/geojson with minor modifications
import countryData from "../../data/countries_new.json";
import { useLocation } from "react-router-dom";
import ErrorBoundary from "../ErrorBoundary";
import t__ from "../../util/lang/util";
import countryView from "../../util/lang/countryView";
import intro from "../../util/lang/intro";
import Container from "../Layout/Container";
import beachScene from "./images/beach-scene.jpg";
import { GLOBAL_ACCEPTANCE_PATH } from "../../routes";

const PREFIX = "Map";
const classes = {
  gm: `${PREFIX}-gm`,
};

// TODO jss-to-styled codemod: The Fragment root was replaced by div. Change the tag if needed.
const Root = styled("div")(({ theme }) => ({
  overflowX: "hidden", // prevent horizontal scroll from carousel negative margins on small screens
  [`& .${classes.gm}`]: {
    opacity: 0,
    transition: "opacity 1s",
    "& button.gm-ui-hover-effect": {
      display: "none !important",
    },
    "& .gm-style iframe + div": { border: "none! important" },
    "& .zoom-control": {
      borderRadius: 8,
      overflow: "hidden",
      marginBottom: 30,
      marginRight: 30,
      boxShadow: "0 2px 4px 0 rgba(0, 0, 0, 0.2)",
      "& button": {
        height: 40,
        width: 40,
        border: "none",
        background: theme.palette.common.white,
        display: "block",
        color: theme.palette.secondary.main,
        fontSize: 36,
        lineHeight: 0,
        fontFamily:
          "Arial, sans-serif" /*using basic font here intentionally for plus and minus signs*/,
        [theme.breakpoints.down("sm")]: {
          display: "none",
        },
        "&:first-child": {
          borderBottom: `1px solid ${theme.palette.borders.light}`,
        },
        "&:hover": {
          cursor: "pointer",
          backgroundColor: "#EFEFF1",
        },
        "&:active": {
          backgroundColor: "#C8C8CF",
        },
        "&:focus": {
          cursor: "pointer",
          outline: "none",
          backgroundColor: theme.palette.common.white,
        },
        "&:focus-visible": {
          cursor: "pointer",
          color: `${theme.palette.primary.main}`,
          outline: "none",
          backgroundColor: theme.palette.common.white,
          boxShadow: `inset 0 0 0 2px ${theme.palette.common.white}, inset 0 0 0 4px ${theme.palette.focus}`,
        },
      },
    },
  },
}));

const mapPulse = keyframes`
  0%, 100% { opacity: 0.15; }
  50% { opacity: 0.35; }
`;

const MapLoadingOverlay = styled(Box)(({ theme, pulseColor }) => ({
  position: "absolute",
  inset: 0,
  backgroundColor: pulseColor,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  animation: `${mapPulse} 1.8s ease-in-out infinite`,
}));

const Map = ({ acceptanceData, ...props }) => {
  const theme = useTheme();
  const mapColorsForLoader = MAPCOLORS.default;
  const mapLoaderPulseColor =
    mapColorsForLoader["J"] ||
    mapColorsForLoader["H"] ||
    theme.palette.secondary.main;
  const location = useLocation();
  const mdUp = useMediaQuery((theme) => theme.breakpoints.up("md"));

  const {
    google,
    selectedCountry,
    setSelectedCountry,
    setAcceptanceData,
    mapLoading,
    setMapLoading,
    activeTooltip,
    tooltipFocusTargetRef,
  } = useContext(MapContext);

  const zoom = getInitialZoom();
  const [fadeMap, setFadeMap] = useState(false);
  const [countryDetailsModalOpen, setCountryDetailsModalOpen] = useState(false);
  const [countryDetailsData, setCountryDetailsData] = useState(null);
  const lastProcessedCountryCode = useRef(null);

  // Set context from props; update when API data arrives after initial load
  useEffect(() => {
    setAcceptanceData(acceptanceData ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [acceptanceData]);

  const { ref, map } = useGoogleMaps(google, {
    center: {
      lat: typeof window !== "undefined" && window.innerWidth < 1200 ? 20 : 30,
      lng:
        typeof window !== "undefined" && window.innerWidth < 1200 ? -100 : 11,
    },
    zoom: zoom,
    minZoom: zoom,
    clickableIcons: false,
    disableDefaultUI: true,
    zoomControl: false,
    fullscreenControl: false,
    gestureHandling: "cooperative",
    scrollwheel: false, // let page scroll when pointer is over map; zoom via +/- controls or pinch
    restriction: {
      latLngBounds: {
        north: 85,
        south: -90,
        west: -180,
        east: 180,
      },
      strictBounds: true,
    },
  });

  const countryMapType = useRef();

  useEffect(() => {
    if (google && map) {
      countryMapType.current = new google.maps.StyledMapType(
        COUNTRY_MAP_STYLES,
        {
          name: "Acceptance Map",
        }
      );
      map.data.addGeoJson(countryData);

      // Assign acceptance data (this sets map type, so do it after style decision)
      assignAcceptanceMapData(map, acceptanceData);

      if (countryMapType.current) {
        makeCountryMap(map, countryMapType.current, theme);
      }

      // Function to open country details modal - exposed for JavaScript to call
      const getRegion = (string = "") => {
        if (string.includes("Europe")) return "Europe";
        if (string.includes("Africa") || string.includes("Middle East"))
          return "Africa";
        if (string.includes("Asia")) return "Asia";
        if (string.includes("America")) return "America";
        if (string.includes("Antarctica")) return "Antarctica";
        return "";
      };

      const openCountryDetailsModal = (countryData) => {
        const fullCountry =
          acceptanceData?.find((a) => a.cca3 === countryData.code) || {};
        const region =
          getRegion(fullCountry.region_wb || countryData.region || "") || null;
        const hasCreditCoverage =
          fullCountry.credit_level &&
          fullCountry.credit_level !== "NO COVERAGE";
        const baseCopy =
          countryDrawer[theme.lang].priorityCountries[fullCountry.cca3] ??
          acceptanceLevels[theme.lang].headlinesAlt[fullCountry.credit_level];
        let acceptanceCopy =
          fullCountry.cca3 === "USA" &&
          countryDrawer[theme.lang].usaAcceptanceWithFootnote
            ? countryDrawer[theme.lang].usaAcceptanceWithFootnote
            : baseCopy;
        if (
          typeof acceptanceCopy === "string" &&
          acceptanceCopy.includes("{region}")
        ) {
          const regionLabel =
            (region && countryDrawer[theme.lang].regions?.[region]) ||
            region ||
            acceptanceLevels[theme.lang].regionFallback ||
            "";
          acceptanceCopy = acceptanceCopy.replace("{region}", regionLabel);
        }

        setCountryDetailsData({
          ...fullCountry,
          ...countryData,
          region,
          hasCreditCoverage,
          acceptanceCopy,
        });
        setCountryDetailsModalOpen(true);
      };

      // Store function on window for JavaScript access
      window.openCountryDetailsModal = openCountryDetailsModal;

      countryView_addListeners(
        map,
        theme,
        setSelectedCountry,
        activeTooltip,
        openCountryDetailsModal,
        tooltipFocusTargetRef
      );
    }
  }, [
    acceptanceData,
    theme,
    google,
    map,
    setMapLoading,
    setSelectedCountry,
    activeTooltip,
    tooltipFocusTargetRef,
  ]);

  useEffect(() => {
    if (!fadeMap && map && google && acceptanceData) {
      createCustomZoomControl(map, google);
      setTimeout(() => setFadeMap(true), 300);
    }
  }, [map, google, fadeMap, acceptanceData]);

  // Remove map keyboard shortcuts link from tab order so focus goes to tooltip/zoom next
  useEffect(() => {
    if (!google || !map || !ref.current) return;
    const container = ref.current;
    const removeLinksFromTabOrder = () => {
      container
        .querySelectorAll("a")
        .forEach((a) => a.setAttribute("tabindex", "-1"));
    };
    removeLinksFromTabOrder();
    google.maps.event.addListenerOnce(map, "idle", removeLinksFromTabOrder);
    const observer = new MutationObserver(removeLinksFromTabOrder);
    observer.observe(container, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [google, map, ref]);

  useEffect(() => {
    if (!map) return;
    makeCountryMap(map, countryMapType.current, theme);
    if (acceptanceData) {
      setTimeout(() => setMapLoading(false), 100);
    }
  }, [map, countryMapType, setMapLoading, theme, acceptanceData]);

  // Handle country query parameter from URL
  useEffect(() => {
    if (
      !map ||
      !google ||
      (location.pathname !== GLOBAL_ACCEPTANCE_PATH &&
        location.pathname !== `${GLOBAL_ACCEPTANCE_PATH}/`)
    ) {
      return;
    }
    // Wait for acceptance data so we can set correct credit_level and tooltip text
    if (!acceptanceData) {
      return;
    }

    const params = new URLSearchParams(location.search);
    const countryCode = params.get("country");

    // Only process if there's a country parameter and it's different from what we last processed
    if (
      countryCode &&
      countryCode !== lastProcessedCountryCode.current &&
      map.data
    ) {
      // Find the feature matching the country code (adm0_a3 or iso_a2, e.g. USA or US)
      let targetFeature = null;
      const codeUpper = countryCode.toUpperCase();
      map.data.forEach((feature) => {
        if (targetFeature) return;
        const adm0 = feature.getProperty("adm0_a3");
        const iso2 = feature.getProperty("iso_a2");
        if (
          adm0 === codeUpper ||
          iso2 === codeUpper ||
          adm0 === countryCode ||
          iso2 === countryCode
        ) {
          targetFeature = feature;
        }
      });

      if (targetFeature) {
        // Update the ref to track this country as processed
        lastProcessedCountryCode.current = countryCode;

        // Find the country data using alias-aware lookup (US <-> USA)
        const adm0_a3 = targetFeature.getProperty("adm0_a3");
        const iso_a2 = targetFeature.getProperty("iso_a2");

        const countryData = acceptanceData?.find(
          (a) =>
            a.cca3 === countryCode || a.cca3 === adm0_a3 || a.cca3 === iso_a2
        );
        const region =
          countryData?.region_wb || targetFeature.getProperty("region_wb");

        // Ensure feature has credit_level and translated_name for tooltip (in case assignAcceptanceMapData ran before acceptanceData was ready)
        if (countryData) {
          targetFeature.setProperty(
            "credit_level",
            countryData.credit_level ?? "NO COVERAGE"
          );
          if (countryData.translated_name) {
            targetFeature.setProperty(
              "translated_name",
              countryData.translated_name
            );
          }
        }

        // Set selected country for dropdown
        setSelectedCountry({
          code: countryCode,
          region: region,
        });

        // Trigger country click to update map view
        setTimeout(() => {
          countryClicked(
            map,
            setSelectedCountry,
            activeTooltip,
            theme,
            null,
            targetFeature,
            tooltipFocusTargetRef
          );
        }, 300); // Small delay to ensure map is fully initialized
      }
    } else if (!countryCode) {
      // Reset the ref when there's no country parameter
      lastProcessedCountryCode.current = null;
    }
  }, [
    map,
    google,
    location.pathname,
    location.search,
    acceptanceData,
    setSelectedCountry,
    activeTooltip,
    theme,
    tooltipFocusTargetRef,
  ]);

  // Sync map polygon selection when selectedCountry changes (e.g. from search)
  useEffect(() => {
    if (!map || !theme) return;
    syncMapSelectionToCountry(map, theme, selectedCountry?.code ?? null);
  }, [map, theme, selectedCountry?.code]);

  return (
    <Root>
      <Helmet>
        <title>
          Discover Network Acceptance - Where You Can Use Your Card Worldwide
        </title>
        <meta
          name="description"
          content="See where you can use your card on the Discover® Network in 185+ countries and territories.* Explore global acceptance across top destinations worldwide."
        />

        <link
          rel="canonical"
          href="https://travel.discoverglobalnetwork.com/global-acceptance/"
        />
      </Helmet>
      <Header />
      <main>
        <MapSearchHeader
          map={map}
          zoom={zoom}
          dataReady={Boolean(acceptanceData)}
        />

        <ErrorBoundary>
          <Box position="relative" height="680px" width="100%" top={0}>
            <div
              className={classes.gm}
              ref={ref}
              style={{
                width: "100%",
                height: "100%",
                opacity: fadeMap ? "1" : "0",
              }}
            />
            {fadeMap && <MapLegend />}
            {(!acceptanceData || mapLoading) && (
              <MapLoadingOverlay
                pulseColor={mapLoaderPulseColor}
                aria-label="Loading..."
              />
            )}
          </Box>
          <>
            <div
              data-aos="fade-up"
              data-aos-delay="400"
              data-aos-duration="500"
              data-aos-once="true"
            >
              <Box display="flex" justifyContent="center" my={5} mx={5}>
                <Box textAlign="center">
                  <Typography
                    variant="body2"
                    fontWeight={400}
                    color={theme.palette.text.secondary}
                    id={COUNTRY_HEADLINE_FOOTNOTE_ID}
                    component="p"
                    role="note"
                    tabIndex={-1}
                    sx={{ scrollMarginTop: `${FOOTNOTE_SCROLL_OFFSET_PX}px` }}
                  >
                    *{" "}
                    {t__(
                      theme.lang,
                      countryView,
                      "countryHeadlineFootnoteAdditional"
                    )}
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight={400}
                    color={theme.palette.text.secondary}
                    id={COUNTRY_HEADLINE_FOOTNOTE_ADDITIONAL_ID}
                    component="p"
                    role="note"
                    tabIndex={-1}
                    sx={{ scrollMarginTop: `${FOOTNOTE_SCROLL_OFFSET_PX}px` }}
                  >
                    1. {t__(theme.lang, countryView, "countryHeadlineFootnote")}
                  </Typography>
                </Box>
              </Box>
              <Box
                sx={{
                  marginY: mdUp ? theme.spacing(20) : theme.spacing(8),
                  marginX: theme.spacing(5),
                }}
                display="flex"
                flexDirection="column"
                justifyContent="center"
                alignItems="center"
              >
                <AcceptanceMarks
                  marks={["DCI", "DN"]}
                  variant={mdUp ? "lg" : "default"}
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    flexWrap: "wrap",
                    gap: theme.spacing(4),
                    marginBottom: theme.spacing(8),
                  }}
                  marksStyles={{
                    marginRight: theme.spacing(0),
                  }}
                />
                <Typography
                  variant={mdUp ? "subtitle1" : "h2"}
                  color="textPrimary"
                  align="center"
                  fontWeight={700}
                  maxWidth={mdUp ? "none" : "330px"}
                  component="h2"
                >
                  {t__(theme.lang, countryView, "travelHeadline")}
                </Typography>
                <Typography
                  variant={mdUp ? "h3" : "h4"}
                  color="textPrimary"
                  align="center"
                  maxWidth={820}
                  marginTop={theme.spacing(4)}
                  component="p"
                  lineHeight={1.4}
                >
                  {t__(theme.lang, countryView, "travelHeadlineCopy")}
                </Typography>
              </Box>
            </div>

            <RegionsSquares />
            <Container
              maxWidth="md"
              marginTop={mdUp ? theme.spacing(20) : theme.spacing(4)}
              marginBottom={mdUp ? theme.spacing(20) : theme.spacing(10)}
              mx="auto"
            >
              <Box
                borderRadius={3}
                marginX="auto"
                display="flex"
                flexDirection="column"
                justifyContent="center"
                alignItems="center"
                paddingX={mdUp ? theme.spacing(20) : theme.spacing(6)}
                width="100%"
                maxWidth={{ xs: 400, md: 500, lg: "none" }}
                minHeight={mdUp ? 315 : 215}
                sx={{
                  position: "relative",
                  overflow: "hidden",
                  backgroundColor: "text.primary",
                  backgroundImage: `url(${beachScene})`,
                  backgroundPosition: "center",
                  backgroundSize: "cover",
                  backgroundRepeat: "no-repeat",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    inset: 0,
                    background: "rgba(0, 0, 0, 0.30)",
                    pointerEvents: "none",
                  },
                  "& > *": {
                    position: "relative",
                    zIndex: 1,
                  },
                }}
              >
                <Typography
                  variant={mdUp ? "subtitle1" : "h2"}
                  color="common.white"
                  align="center"
                  fontWeight={700}
                  maxWidth={524}
                  lineHeight={1.4}
                  component="h2"
                  mx="auto"
                  paddingBottom={mdUp ? theme.spacing(10) : theme.spacing(6)}
                >
                  {t__(theme.lang, countryView, "placement.headline")}
                </Typography>
                <Button
                  variant="contained"
                  disableElevation
                  disableRipple
                  onClick={() => {
                    const el = document.getElementById("country-headline-copy");
                    if (!el) return;
                    el.focus({ preventScroll: true });
                    el.scrollIntoView({ behavior: "smooth", block: "center" });
                  }}
                  sx={{
                    bgcolor: "primary.light",
                    color: "common.white",
                    borderRadius: "144px",
                    textTransform: "none",
                    px: 6,
                    py: 3,
                    minHeight: 48,
                    fontSize: 19,
                    fontWeight: "bold",
                    lineHeight: "24px",
                    textAlign: "center",
                    whiteSpace: "nowrap",
                    "&:hover": {
                      bgcolor: "primary.light",
                      filter: "brightness(0.95)",
                    },
                    "&:focus": {
                      outline: "none",
                    },
                    "&:focus-visible": {
                      bgcolor: "primary.light",
                      filter: "brightness(0.95)",
                      outline: `2px solid ${theme.palette.focus}`,
                      outlineOffset: "2px",
                    },
                  }}
                >
                  {t__(theme.lang, countryView, "placement.cta")}
                </Button>
              </Box>
            </Container>
          </>
          <CountryDetailsModal
            open={countryDetailsModalOpen}
            onClose={() => {
              setCountryDetailsModalOpen(false);
              handleCountryDetailsModalClose();
            }}
            countryData={countryDetailsData}
          />
        </ErrorBoundary>
      </main>
      <Footer />
    </Root>
  );
};

Map.propTypes = {
  acceptanceData: PropTypes.array,
  props: PropTypes.object,
};

export default Map;
